import React, { useState, useEffect, useRef } from 'react';
import type { StaffMember } from '../../types';
import XCircleIcon from '../icons/XCircleIcon';
import { useSoftKeyboard } from '../../contexts/SoftKeyboardContext';

interface PinEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPinVerified: (staff: StaffMember) => void;
  getStaffByPin: (pin: string) => StaffMember | undefined;
  error?: string;
}

const PinEntryModal: React.FC<PinEntryModalProps> = ({
  isOpen,
  onClose,
  onPinVerified,
  getStaffByPin,
  error: externalError,
}) => {
  const [pin, setPin] = useState('');
  const pinRef = useRef<string>(''); // Ref to track the latest PIN value
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { openKeyboard, enabled: softKeyboardEnabled } = useSoftKeyboard();
  
  // Update ref whenever pin changes
  useEffect(() => {
    pinRef.current = pin;
  }, [pin]);

  useEffect(() => {
    if (externalError) {
      setError(externalError);
    }
  }, [externalError]);

  useEffect(() => {
    if (isOpen) {
      setPin('');
      setError('');
      setIsProcessing(false);
    }
  }, [isOpen]);

  const handlePinChange = (value: string) => {
    // Only allow digits, max 10 characters
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setPin(digitsOnly);
    pinRef.current = digitsOnly; // Update ref immediately
    setError('');
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    setError('');
    
    // Use ref value as it's always the most up-to-date
    const currentPin = pinRef.current || pin;
    
    if (!currentPin || currentPin.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }

    setIsProcessing(true);
    
    // Validate PIN using the ref value
    const staff = getStaffByPin(currentPin);
    
    if (!staff) {
      setError('Invalid PIN. Please try again.');
      setIsProcessing(false);
      return;
    }

    // PIN is valid - allow both managers and non-managers to use PIN
    // Managers can use PIN for quick access to POS/KDS/Service screens
    setIsProcessing(false);
    onPinVerified(staff);
    setPin('');
    pinRef.current = '';
  };

  // Separate handler for soft keyboard submission (no event needed)
  const handleSoftKeyboardSubmit = () => {
    // Use requestAnimationFrame to ensure the onChange state update has completed
    requestAnimationFrame(() => {
      // Double-check we have the latest value from ref
      const finalPin = pinRef.current;
      if (finalPin !== pin) {
        setPin(finalPin);
      }
      handleSubmit();
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e as any);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto my-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Enter PIN</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XCircleIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="pin" className="block text-sm font-medium text-gray-700 mb-2">
                Staff PIN
              </label>
              <input
                id="pin"
                type="password"
                value={pin}
                onChange={(e) => handlePinChange(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => {
                  if (softKeyboardEnabled) {
                    // Use ref value to ensure we have the latest
                    const currentValue = pinRef.current || pin;
                    openKeyboard({
                      id: 'pin-entry',
                      type: 'numeric',
                      value: currentValue,
                      label: 'Enter PIN',
                      onChange: handlePinChange,
                      maskValue: true,
                      onSubmit: handleSoftKeyboardSubmit,
                    });
                  }
                }}
                readOnly={softKeyboardEnabled}
                className="w-full px-4 py-3 text-2xl text-center border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-gray-50 text-gray-900 font-mono tracking-widest"
                placeholder="Enter PIN"
                autoFocus
                disabled={isProcessing}
                maxLength={10}
                inputMode="numeric"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                data-form-type="other"
                data-lpignore="true"
                data-1p-ignore="true"
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Enter your assigned PIN to access the system
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isProcessing || !pin || pin.length < 4}
                className="flex-1 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Verifying...' : 'Enter'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PinEntryModal;

